import { useEffect, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { format, isValid, parseISO } from "date-fns";
import { Download, RotateCw } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { api, ApiError, type LogFile, type LogLine, type Page } from "@/lib/api";
import { useServerTable } from "@/lib/table";
import { dateTime } from "@/lib/format";
import {
  Badge,
  Button,
  Card,
  Checkbox,
  Dialog,
  Empty,
  ErrorNote,
  Input,
  Select,
  SortableTh,
  Spinner,
  Table,
  TableFooter,
  Td,
  Th,
  Toolbar,
  type Tone,
} from "@/components/ui";

// The log files as obs writes them, read back: one file at a time, parsed
// into its columns, searched and paged on the server. The legacy viewer
// loaded the whole file into the browser, which is how billing.log came to
// time out; here the browser only ever holds one page.

type Listing = { dir: string; files: LogFile[] };

const LEVELS = [
  { id: "", label: "alle Stufen" },
  { id: "trace", label: "ab Trace" },
  { id: "debug", label: "ab Debug" },
  { id: "info", label: "ab Info" },
  { id: "warn", label: "ab Warnung" },
  { id: "error", label: "ab Fehler" },
  { id: "fatal", label: "nur Kritisch" },
];

export function LogFiles() {
  const [params, setParams] = useSearchParams();

  const listing = useQuery({
    queryKey: ["logs", "files"],
    queryFn: () => api<Listing>("/api/admin/logs/files"),
    refetchInterval: 30_000,
  });
  const files = listing.data?.files ?? [];

  // The file is part of the URL so that a line somebody found can be sent
  // around as a link. Without one, the combined log is the place to start.
  const requested = params.get("file") ?? "";
  const file = files.some((f) => f.name === requested)
    ? requested
    : (files.find((f) => f.name === "all.log") ?? files[0])?.name ?? "";
  const setFile = (name: string) => {
    const next = new URLSearchParams(params);
    next.set("file", name);
    setParams(next, { replace: true });
  };

  // Typing is cheap; scanning a sixty-megabyte file per keystroke is not, so
  // the search reaches the server a beat after the typing stops.
  const [search, setSearch] = useState("");
  const [q, setQ] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setQ(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);
  const [regex, setRegex] = useState(false);
  const [level, setLevel] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [live, setLive] = useState(false);
  const [selected, setSelected] = useState<LogLine | null>(null);

  const [total, setTotal] = useState(0);
  const table = useServerTable({
    total,
    initial: { key: "at", dir: "desc" },
    resetOn: [file, q, regex, level, from, to],
  });

  const lines = useQuery({
    queryKey: ["logs", "file", file, q, regex, level, from, to, table.query],
    queryFn: () =>
      api<Page<LogLine>>(
        `/api/admin/logs/files/${encodeURIComponent(file)}?${table.query}` +
          `&q=${encodeURIComponent(q)}${regex ? "&re=1" : ""}` +
          `&level=${level}&from=${from}&to=${to}`,
      ),
    enabled: file !== "",
    refetchInterval: live ? 5_000 : false,
    // Keep the last page on screen while the next one loads, so that a
    // refresh does not blank the table every five seconds.
    placeholderData: (prev) => prev,
  });

  useEffect(() => {
    if (lines.data) setTotal(lines.data.total);
  }, [lines.data]);

  const current = files.find((f) => f.name === file);

  if (listing.isLoading) return <Spinner />;
  if (files.length === 0) {
    return (
      <Empty
        title="Keine Log-Dateien"
        hint="MBM_LOG_DIR ist nicht gesetzt, oder das Verzeichnis ist leer."
      />
    );
  }

  return (
    <Card className="p-4">
      <Toolbar>
        <Select
          className="w-72"
          value={file}
          onChange={(e) => setFile(e.target.value)}
          aria-label="Datei"
        >
          {files.map((f) => (
            <option key={f.name} value={f.name}>
              {f.name} · {bytes(f.size)} · {dateTime(f.modifiedAt)}
            </option>
          ))}
        </Select>
        <Input
          className="w-64 font-mono"
          placeholder={regex ? "regulärer Ausdruck" : "Suche in der Zeile"}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Suche"
        />
        <Checkbox
          label="Regex"
          checked={regex}
          onChange={(e) => setRegex(e.target.checked)}
        />
        <Select
          className="w-36"
          value={level}
          onChange={(e) => setLevel(e.target.value)}
          aria-label="Stufe"
        >
          {LEVELS.map((l) => (
            <option key={l.id} value={l.id}>
              {l.label}
            </option>
          ))}
        </Select>
        <Input
          type="datetime-local"
          className="w-48"
          value={from}
          onChange={(e) => setFrom(e.target.value)}
          aria-label="von"
        />
        <span className="text-sm muted">bis</span>
        <Input
          type="datetime-local"
          className="w-48"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          aria-label="bis"
        />
        <Checkbox
          label="alle 5 s aktualisieren"
          checked={live}
          onChange={(e) => setLive(e.target.checked)}
        />
        <Button size="sm" onClick={() => void lines.refetch()}>
          <RotateCw size={14} /> Aktualisieren
        </Button>
        <a
          className="inline-flex h-7 items-center gap-1.5 rounded-md border surface px-2.5 text-xs font-medium shadow-xs hover:bg-[var(--surface-sunken)]"
          href={`/api/admin/logs/files/${encodeURIComponent(file)}/download`}
          download
        >
          <Download size={14} /> Herunterladen
        </a>
      </Toolbar>

      <p className="mb-2 text-xs muted">
        {listing.data?.dir}/{file}
        {current && ` · ${bytes(current.size)} · zuletzt ${dateTime(current.modifiedAt)}`}
        {current?.rotated && " · rotiert, wird nicht mehr geschrieben"}
      </p>

      {lines.error && (
        <ErrorNote>
          {lines.error instanceof ApiError
            ? (lines.error.fields?.q ?? lines.error.detail)
            : String(lines.error)}
        </ErrorNote>
      )}

      {lines.isLoading ? (
        <Spinner />
      ) : (
        <Table dense>
          <thead>
            <tr>
              <SortableTh table={table} sortKey="at" first="desc">
                Zeit
              </SortableTh>
              <Th>Stufe</Th>
              <Th>Vorgang</Th>
              <Th>Meldung</Th>
              <Th>Korrelation</Th>
            </tr>
          </thead>
          <tbody>
            {(lines.data?.items ?? []).map((l) =>
              l.parsed ? (
                <tr
                  key={l.n}
                  className="cursor-pointer hover:bg-[var(--surface-sunken)]"
                  onClick={() => setSelected(l)}
                >
                  <Td className="whitespace-nowrap font-mono text-xs muted">
                    {stamp(l.at)}
                  </Td>
                  <Td>
                    <Badge tone={tone(l.level)}>{l.level}</Badge>
                  </Td>
                  <Td className="font-mono text-xs">{l.op || "—"}</Td>
                  <Td>
                    <span>{highlight(l.message, q, regex)}</span>
                    <Attrs attrs={l.attrs} />
                  </Td>
                  <Td>
                    {l.correlation && (
                      <button
                        type="button"
                        className="font-mono text-xs muted hover:underline"
                        title={`Alle Zeilen zu ${l.correlation}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setRegex(false);
                          setSearch(l.correlation);
                        }}
                      >
                        {l.correlation.slice(0, 8)}
                      </button>
                    )}
                  </Td>
                </tr>
              ) : (
                <tr
                  key={l.n}
                  className="cursor-pointer hover:bg-[var(--surface-sunken)]"
                  onClick={() => setSelected(l)}
                >
                  <Td className="font-mono text-xs muted" colSpan={5}>
                    {highlight(l.raw, q, regex)}
                  </Td>
                </tr>
              ),
            )}
          </tbody>
        </Table>
      )}
      {!lines.isLoading && !lines.error && (lines.data?.items ?? []).length === 0 && (
        <Empty title="Keine Zeilen" hint="Nichts in dieser Datei passt zu den Filtern." />
      )}
      <TableFooter table={table} noun="Zeilen" />

      {selected && (
        <LineDialog line={selected} file={file} onClose={() => setSelected(null)} />
      )}
    </Card>
  );
}

/** The first few key=value pairs inline; the dialog has the rest. */
function Attrs({ attrs }: { attrs: LogLine["attrs"] }) {
  if (attrs.length === 0) return null;
  const shown = attrs.slice(0, 3);
  const more = attrs.length - shown.length;
  return (
    <span className="ml-2 inline-flex flex-wrap gap-x-2 font-mono text-xs muted">
      {shown.map((a, i) => (
        <span key={i} className="max-w-64 truncate" title={`${a.key}=${a.value}`}>
          {a.key ? `${a.key}=${a.value}` : a.value}
        </span>
      ))}
      {more > 0 && <span>+{more}</span>}
    </span>
  );
}

function LineDialog({
  line,
  file,
  onClose,
}: {
  line: LogLine;
  file: string;
  onClose: () => void;
}) {
  const fields: [string, ReactNode][] = line.parsed
    ? [
        ["Zeit", stamp(line.at)],
        ["Stufe", <Badge tone={tone(line.level)}>{line.level}</Badge>],
        ["Vorgang", line.op || "—"],
        ["Meldung", line.message],
        ["Prozess", line.pid],
        ["Laufzeit", `${line.exec.toFixed(3)} s seit Start`],
        ["Sitzung", line.session || "—"],
        ["Korrelation", line.correlation || "—"],
        ["Benutzer", line.userId || "—"],
      ]
    : [];

  return (
    <Dialog open onClose={onClose} title={`${file}, Zeile ${line.n}`} size="lg">
      {line.parsed && (
        <dl className="mb-3 grid grid-cols-[8rem_1fr] gap-x-3 gap-y-1 text-sm">
          {fields.map(([k, v]) => (
            <FieldRow key={k} label={k}>
              {v}
            </FieldRow>
          ))}
          {line.attrs.map((a, i) => (
            <FieldRow key={`a${i}`} label={a.key || "·"} mono>
              {a.value}
            </FieldRow>
          ))}
        </dl>
      )}
      <pre className="max-h-64 overflow-auto rounded-md border bg-[var(--surface-sunken)] p-2 font-mono text-xs whitespace-pre-wrap break-all">
        {line.raw}
      </pre>
    </Dialog>
  );
}

function FieldRow({
  label,
  mono,
  children,
}: {
  label: string;
  mono?: boolean;
  children: ReactNode;
}) {
  return (
    <>
      <dt className="truncate font-mono text-xs muted" title={label}>
        {label}
      </dt>
      <dd className={mono ? "font-mono text-xs break-all" : "break-words"}>{children}</dd>
    </>
  );
}

function tone(level: string): Tone {
  switch (level) {
    case "FATAL":
    case "ERROR":
      return "danger";
    case "WARN":
      return "warning";
    case "INFO":
      return "info";
    default:
      return "neutral";
  }
}

/** The file's own precision: a second is too coarse for two lines of one request. */
function stamp(iso: string): string {
  const d = parseISO(iso);
  return isValid(d) ? format(d, "dd.MM. HH:mm:ss.SSS") : "—";
}

function bytes(v: number): string {
  if (v < 1024) return `${v} B`;
  if (v < 1024 * 1024) return `${Math.round(v / 1024)} kB`;
  return `${(v / 1024 / 1024).toFixed(1)} MB`;
}

/** Marks what the search matched, so that a hit in a long line is findable. */
function highlight(text: string, q: string, regex: boolean): ReactNode {
  if (!q) return text;
  let re: RegExp;
  try {
    re = new RegExp(regex ? q : q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "gi");
  } catch {
    return text;
  }
  const out: ReactNode[] = [];
  let last = 0;
  for (const m of text.matchAll(re)) {
    if (m[0] === "") break; // an empty match would never advance
    const at = m.index ?? 0;
    if (at > last) out.push(text.slice(last, at));
    out.push(
      <mark key={at} className="rounded-sm bg-amber-200 px-0.5 dark:bg-amber-500/40 dark:text-inherit">
        {m[0]}
      </mark>,
    );
    last = at + m[0].length;
  }
  if (out.length === 0) return text;
  if (last < text.length) out.push(text.slice(last));
  return out;
}
